import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { captureVideoState, formatInitialStateAsGitDiff } from '../video-state';
import { reportIfVideoStateOversized } from '../videoStateSizeGuard';

import { Tool } from './Tool';

export const getVideoStateSchema = z.object({});
export type GetVideoStateArgs = z.infer<typeof getVideoStateSchema>;

/**
 * Tool that returns the current video state.
 * Used by agents to verify the actual state of the video during cleanup/review phases.
 */
export class GetVideoStateTool extends Tool<typeof getVideoStateSchema> {
    protected override readonly schema = getVideoStateSchema;
    protected override readonly toolName = TOOL_NAMES.getVideoState;
    protected override readonly toolDescription =
        'Returns the current video state showing all scenes, their text content, and media. Use this during review/cleanup to verify what is actually in the video.';

    public override getToolDisplayName(): string {
        return 'Getting video state';
    }

    public get statusIcon(): string {
        return 'movie_info';
    }

    public async execute(_args: Record<string, never>, context: ToolExecutionContext) {
        try {
            // Capture in the hosting agent's color-name mode so this explicit re-read matches the
            // auto-injected video state (bare hex for the toolbox, hex + name for legacy).
            const includeColorNames = context.videoStateManager?.getIncludeColorNames() ?? true;
            const snapshot = await captureVideoState(
                this.luminary,
                undefined,
                this.getState,
                includeColorNames,
            );
            // Render in the hosting agent's addressing mode so this explicit re-read matches the
            // auto-injected video state (scene_id/scene_group_id for the toolbox, index for legacy).
            const sceneAddressing = context.videoStateManager?.getSceneAddressing() ?? 'index';
            const formattedState = formatInitialStateAsGitDiff(snapshot, sceneAddressing);
            // This on-demand re-read renders the full, untruncated state, so it can surface a video
            // that has grown large mid-conversation — a size the small incremental diffs never show.
            reportIfVideoStateOversized(
                formattedState,
                context.model,
                TOOL_NAMES.getVideoState,
                context.task ?? 'unknown',
            );
            return formattedState;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error getting video state: ${errorMessage}`;
        }
    }
}
